import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
} from '@mui/material';
import roleAdminService from '../../services/roleAdminService';

// Stessa regola che il backend applica al codice di un ruolo: validarla qui evita un giro di
// rete per un errore che si vede a occhio.
const CODE_PATTERN = /^[a-z][a-z0-9_]{2,49}$/;
const CODE_HELP =
    'Il codice deve essere minuscolo, iniziare con una lettera e contenere solo lettere, cifre e underscore, da 3 a 50 caratteri.';
const CODE_MAX_LENGTH = 50;
const SUFFISSO_COPIA = '_copia';

// Un codice sorgente vicino al limite di 50 caratteri, con "_copia" appeso, lo supererebbe:
// il campo si aprirebbe gia' invalido e l'utente lo scoprirebbe solo al salvataggio. Si
// tronca la parte presa dal sorgente per lasciare sempre spazio al suffisso.
function suggerisciCodiceDuplicato(sourceCode) {
    const base = (sourceCode || '').slice(0, CODE_MAX_LENGTH - SUFFISSO_COPIA.length);
    return `${base}${SUFFISSO_COPIA}`;
}

const TITOLI = {
    create: 'Nuovo ruolo',
    duplicate: 'Duplica ruolo',
    rename: 'Rinomina ruolo',
};

const ETICHETTE_PULSANTE = {
    create: 'Crea',
    duplicate: 'Duplica',
    rename: 'Salva',
};

/**
 * Dialogo di creazione, duplicazione e rinomina di un ruolo (spec §10.2, §10.3).
 *
 * La modalita' e' esplicita in `mode`, non dedotta dalla sola presenza di `source`:
 * `create` (nessun sorgente), `duplicate` (precompila da un ruolo sorgente, il codice resta
 * modificabile perche' duplicare serve proprio a scostarsi dall'originale) e `rename` (nessun
 * codice: si cambiano solo etichetta e descrizione di un ruolo personalizzato esistente,
 * chiamando `roleAdminService.updateRole`).
 */
export default function NewRoleDialog({ open, mode, source, onClose, onCreated }) {
    const [code, setCode] = useState('');
    const [label, setLabel] = useState('');
    const [description, setDescription] = useState('');
    const [saving, setSaving] = useState(false);
    const [validationError, setValidationError] = useState(null);
    const [serverError, setServerError] = useState(null);

    const effectiveMode = mode || (source ? 'duplicate' : 'create');
    const showCode = effectiveMode !== 'rename';
    const showDescription = effectiveMode !== 'duplicate';

    useEffect(() => {
        if (open) {
            if (effectiveMode === 'duplicate') {
                setCode(suggerisciCodiceDuplicato(source?.code));
                setLabel(`${source?.label || ''} (copia)`);
                setDescription(source?.description || '');
            } else if (effectiveMode === 'rename') {
                setCode('');
                setLabel(source?.label || '');
                setDescription(source?.description || '');
            } else {
                setCode('');
                setLabel('');
                setDescription('');
            }
            setValidationError(null);
            setServerError(null);
            setSaving(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, source, effectiveMode]);

    const handleSubmit = () => {
        setServerError(null);
        if (showCode && !CODE_PATTERN.test(code)) {
            setValidationError(CODE_HELP);
            return;
        }
        setValidationError(null);
        setSaving(true);

        let payload;
        if (effectiveMode === 'duplicate') {
            payload = roleAdminService.duplicateRole(source.id, { code, label });
        } else if (effectiveMode === 'rename') {
            payload = roleAdminService.updateRole(source.id, { label, description });
        } else {
            payload = roleAdminService.createRole({ code, label, description });
        }

        payload.then(
            (saved) => {
                setSaving(false);
                onCreated(saved);
            },
            (err) => {
                setSaving(false);
                setServerError(
                    err?.response?.data?.message ||
                        'Non è stato possibile salvare il ruolo.',
                );
            },
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{TITOLI[effectiveMode]}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {showCode && (
                        <TextField
                            label="Codice"
                            value={code}
                            onChange={(event) => setCode(event.target.value)}
                            helperText={validationError || CODE_HELP}
                            error={Boolean(validationError)}
                            fullWidth
                        />
                    )}
                    <TextField
                        label="Etichetta"
                        value={label}
                        onChange={(event) => setLabel(event.target.value)}
                        fullWidth
                    />
                    {showDescription && (
                        <TextField
                            label="Descrizione"
                            value={description}
                            onChange={(event) => setDescription(event.target.value)}
                            multiline
                            minRows={2}
                            fullWidth
                        />
                    )}
                    {serverError && <Alert severity="error">{serverError}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Annulla</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={saving}>
                    {ETICHETTE_PULSANTE[effectiveMode]}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

NewRoleDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    mode: PropTypes.oneOf(['create', 'duplicate', 'rename']),
    source: PropTypes.shape({
        id: PropTypes.string,
        code: PropTypes.string,
        label: PropTypes.string,
        description: PropTypes.string,
    }),
    onClose: PropTypes.func.isRequired,
    onCreated: PropTypes.func.isRequired,
};

NewRoleDialog.defaultProps = {
    mode: null,
    source: null,
};
